// Canvas container-marker generator — two-tone pill with status border, label, and type-icon region.
import { common, grey } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'

const SCALE = 1
const OVERSAMPLE = 2

// Outer height matches the DOM MUI Chip (32px), so the WebGL pill reads at the same on-screen size.
const OUTER_H = 32 * SCALE
const TIP_H = 8 * SCALE
const BUFFER = 4 * SCALE
const SHADOW_PAD = 6 * SCALE // canvas padding so the drop shadow isn't clipped
export const CONTAINER_PILL_CSS_HEIGHT = OUTER_H + TIP_H + BUFFER + SHADOW_PAD

const BORDER_W = 2 * SCALE
const INSET_RING_W = 1.5 * SCALE
const ICON_COLUMN_W = 30 * SCALE
const ICON_SQUARE_SIZE = 22 * SCALE
const ICON_SQUARE_RADIUS = 4 * SCALE
const LABEL_MIN_W = 44 * SCALE
const LABEL_PAD_X = 14 * SCALE
const DASH_ON = 4 * SCALE
const DASH_OFF = 3 * SCALE
const RADIUS = (OUTER_H / 2) * SCALE
const TIP_W = 16 * SCALE
const ICON_DRAW_SIZE = 16 * SCALE

export type ContainerPillIconOptions = {
  label?: string
  iconCanvas?: HTMLCanvasElement | null
  outerColor?: string
  innerBgColor?: string
  labelColor?: string
  iconBgColor?: string
  iconColor?: string
  outlineColor?: string | null
  // Above the 2px default a white gap is drawn between pill and ring, so a selection reads against a coloured status border.
  outlineWidth?: number
  // A placement is not physically there, so its border is dashed rather than a solid ring.
  dashed?: boolean
}

export type ContainerPillIcon = {
  canvas: HTMLCanvasElement
  width: number
  height: number
}

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  // Only a lost or unsupported 2d context returns null, and nothing downstream can draw without one.
  if (!ctx) throw new Error('generateContainerPillIcon: 2d canvas context unavailable')
  return { canvas, ctx }
}

// Lazy so importing the height constant never touches a canvas — jsdom has none.
let measureCtx: CanvasRenderingContext2D | null = null
const getMeasureCtx = () => {
  if (!measureCtx) measureCtx = createCanvas(1, 1).ctx
  return measureCtx
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, fill: string) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
  ctx.fillStyle = fill
  ctx.fill()
}

// LRU cap on the per-source tint cache so long-lived sessions with many distinct colors don't accumulate canvases without bound.
const TINT_CACHE_MAX_PER_SOURCE = 32
const tintCache = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>()
function tintCanvas(source: HTMLCanvasElement, color: string) {
  let byColor = tintCache.get(source)
  if (!byColor) {
    byColor = new Map()
    tintCache.set(source, byColor)
  }
  const cached = byColor.get(color)
  if (cached) {
    // Re-insert so LRU order reflects most-recent use.
    byColor.delete(color)
    byColor.set(color, cached)
    return cached
  }

  const { canvas, ctx } = createCanvas(source.width, source.height)
  ctx.drawImage(source, 0, 0)
  ctx.globalCompositeOperation = 'source-in'
  ctx.fillStyle = color
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  byColor.set(color, canvas)
  if (byColor.size > TINT_CACHE_MAX_PER_SOURCE) {
    const oldestKey = byColor.keys().next().value
    if (oldestKey !== undefined) byColor.delete(oldestKey)
  }
  return canvas
}

export function generateContainerPillIcon({
  label = '',
  iconCanvas = null,
  outerColor = grey[500],
  innerBgColor = common.white,
  labelColor = common.black,
  iconBgColor = grey[300],
  iconColor = common.white,
  outlineColor = null,
  outlineWidth = 2,
  dashed = false,
}: ContainerPillIconOptions): ContainerPillIcon {
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE

  const fontSize = 16 * SCALE
  const measure = getMeasureCtx()
  measure.font = `700 ${fontSize}px "Inter", sans-serif`
  const textW = measure.measureText(String(label)).width
  const labelW = Math.max(LABEL_MIN_W, textW + LABEL_PAD_X)
  const innerW = ICON_COLUMN_W + labelW
  const outerW = innerW + BORDER_W * 2
  const outlineW = outlineColor ? outlineWidth * SCALE : 0
  // Only wide rings get the separator; at 2px there is no room for one.
  const separatorW = outlineColor && outlineWidth > 2 ? 2 * SCALE : 0

  const totalW = outerW + 4 * SCALE + SHADOW_PAD * 2 + outlineW * 2
  const totalH = OUTER_H + TIP_H + BUFFER + SHADOW_PAD + outlineW * 2

  const { canvas, ctx } = createCanvas(Math.ceil(totalW * dpi), Math.ceil(totalH * dpi))
  ctx.scale(dpi, dpi)
  // Set the font once up-front so any later text op uses the right font even if the label branch is skipped.
  ctx.font = `700 ${fontSize}px "Inter", sans-serif`

  const cx = totalW / 2
  const cy = OUTER_H / 2 + SCALE + outlineW
  const outerX = cx - outerW / 2
  const outerY = cy - OUTER_H / 2
  const innerX = outerX + BORDER_W
  const innerY = outerY + BORDER_W
  const innerH = OUTER_H - BORDER_W * 2

  if (outlineColor && outlineW > 0) {
    drawRoundedRect(
      ctx,
      outerX - outlineW,
      outerY - outlineW,
      outerW + outlineW * 2,
      OUTER_H + outlineW * 2,
      RADIUS + outlineW,
      outlineColor
    )
  }

  // Drawn over the ring and under the pill, so it reads as a gap rather than a band.
  if (separatorW > 0) {
    drawRoundedRect(
      ctx,
      outerX - separatorW,
      outerY - separatorW,
      outerW + separatorW * 2,
      OUTER_H + separatorW * 2,
      RADIUS + separatorW,
      common.white
    )
  }

  // Drop shadow matches the DOM chip's boxShadow 0 2px 4px rgba(0,0,0,0.3).
  ctx.save()
  ctx.shadowOffsetY = 2
  ctx.shadowBlur = 4
  ctx.shadowColor = alpha(common.black, 0.3)
  drawRoundedRect(ctx, outerX, outerY, outerW, OUTER_H, RADIUS, dashed ? innerBgColor : outerColor)
  ctx.restore()

  ctx.save()
  ctx.beginPath()
  ctx.roundRect(innerX, innerY, innerW, innerH, RADIUS - 1)
  ctx.clip()
  // Inner bg fills first; the colored icon square is drawn inset on top so chip bg shows around it.
  drawRoundedRect(ctx, innerX, innerY, innerW, innerH, 0, innerBgColor)
  const iconSquareX = innerX + labelW + (ICON_COLUMN_W - ICON_SQUARE_SIZE) / 2
  const iconSquareY = innerY + (innerH - ICON_SQUARE_SIZE) / 2
  drawRoundedRect(ctx, iconSquareX, iconSquareY, ICON_SQUARE_SIZE, ICON_SQUARE_SIZE, ICON_SQUARE_RADIUS, iconBgColor)
  ctx.restore()

  // White inset ring (matches the DOM chip's inset 0 0 0 1.5px white).
  ctx.save()
  ctx.strokeStyle = common.white
  ctx.lineWidth = INSET_RING_W
  ctx.beginPath()
  ctx.roundRect(
    innerX + INSET_RING_W / 2,
    innerY + INSET_RING_W / 2,
    innerW - INSET_RING_W,
    innerH - INSET_RING_W,
    Math.max(0, RADIUS - 1 - INSET_RING_W / 2)
  )
  ctx.stroke()
  ctx.restore()

  // Stroked last so it sits over the inset ring, in the band the solid border would have filled.
  if (dashed) {
    ctx.save()
    ctx.strokeStyle = outerColor
    ctx.lineWidth = BORDER_W
    ctx.setLineDash([DASH_ON, DASH_OFF])
    ctx.beginPath()
    ctx.roundRect(outerX + BORDER_W / 2, outerY + BORDER_W / 2, outerW - BORDER_W, OUTER_H - BORDER_W, RADIUS - BORDER_W / 2)
    ctx.stroke()
    ctx.restore()
  }

  if (label) {
    ctx.fillStyle = labelColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(label), innerX + labelW / 2, cy + 0.5 * SCALE)
  }

  if (iconCanvas) {
    const iconX = innerX + labelW + (ICON_COLUMN_W - ICON_DRAW_SIZE) / 2
    const iconY = innerY + (innerH - ICON_DRAW_SIZE) / 2
    const tinted = tintCanvas(iconCanvas, iconColor)
    ctx.drawImage(tinted, iconX, iconY, ICON_DRAW_SIZE, ICON_DRAW_SIZE)
  }

  const tipBaseY = cy + OUTER_H / 2 - 1.5 * SCALE
  if (outlineColor && outlineW > 0) {
    ctx.beginPath()
    ctx.moveTo(cx - (TIP_W / 2 + outlineW), tipBaseY)
    ctx.lineTo(cx + (TIP_W / 2 + outlineW), tipBaseY)
    ctx.lineTo(cx, tipBaseY + TIP_H + outlineW)
    ctx.closePath()
    ctx.fillStyle = outlineColor
    ctx.fill()
  }
  ctx.beginPath()
  ctx.moveTo(cx - TIP_W / 2, tipBaseY)
  ctx.lineTo(cx + TIP_W / 2, tipBaseY)
  ctx.lineTo(cx, tipBaseY + TIP_H)
  ctx.closePath()
  ctx.fillStyle = outerColor
  ctx.fill()

  // Return actual pixel dims so the atlas aligns with the dpi-scaled source; IconLayer.getSize rescales on screen.
  return { canvas, width: canvas.width, height: canvas.height }
}
